/** Row-level INTENT for the whole-array settings ledgers (TXNEXP, EXP2,
 * COMPANY_EXP, INVESTORS, BUYERS, PARTNERS, …).
 *
 * The door only takes the WHOLE array, and every tab builds that array from
 * the copy it loaded — once per session. So a tab open since Monday that adds
 * one expense on Wednesday sends Monday's list plus one row, and everything
 * the phone logged in between is gone. The Phase-26 etag guard catches that
 * write, but the self-heal then re-sent the same stale array with a fresh
 * tag ("last write wins"), i.e. the clobber went through anyway.
 * 2026-09-02, Tom: "im trying to add a new expense to 11441 crest lake and
 * it doesnt add" — the add landed, then a stale array overwrote it.
 *
 * Fix: a ledger put carries WHAT CHANGED (rows added, rows removed) next to
 * the array. Delivery re-reads the server's current copy and re-applies just
 * that delta onto it. Rows are matched by fingerprint (canonical JSON), so an
 * add is idempotent (a re-sent batch can't double a row) and a removal only
 * removes the row it meant to. New TXNEXP rows get a `cid` so two otherwise
 * identical expenses stay two rows. */

export interface LedgerIntent {
  /** rows this tab added — `at` = where it put them (0 = front) */
  added: { row: unknown; at: number }[];
  /** fingerprints of rows this tab removed (an edit = remove old + add new) */
  removed: string[];
}

/** Canonical fingerprint: objects with sorted keys, so key order never makes
 * the same row look different. */
export function ledgerFp(row: unknown): string {
  return JSON.stringify(canon(row));
}
function canon(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canon);
  if (v !== null && typeof v === "object") {
    const o = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(o).sort()) {
      if (o[k] !== undefined) out[k] = canon(o[k]);
    }
    return out;
  }
  return v;
}

const countBy = (rows: unknown[]): Map<string, number> => {
  const m = new Map<string, number>();
  for (const r of rows) {
    const fp = ledgerFp(r);
    m.set(fp, (m.get(fp) ?? 0) + 1);
  }
  return m;
};

/** What changed between the copy this tab held and the array it now wants
 * to write. Multiset diff: a row present twice before and once after counts
 * as ONE removal. */
export function diffLedger(prev: unknown[], next: unknown[]): LedgerIntent {
  const before = countBy(prev);
  const added: LedgerIntent["added"] = [];
  const seen = new Map<string, number>();
  next.forEach((row, at) => {
    const fp = ledgerFp(row);
    const n = (seen.get(fp) ?? 0) + 1;
    seen.set(fp, n);
    if (n > (before.get(fp) ?? 0)) added.push({ row, at });
  });
  const removed: string[] = [];
  for (const [fp, n] of before) {
    const after = seen.get(fp) ?? 0;
    for (let i = after; i < n; i++) removed.push(fp);
  }
  return { added, removed };
}

/** Two queued puts on the same key fold into one (the outbox coalesces); the
 * intents fold with them. A row added by `a` and removed again by `b` cancels
 * out instead of surviving as a removal of something the server never saw. */
export function composeIntent(a: LedgerIntent, b: LedgerIntent): LedgerIntent {
  const added = [...a.added];
  const removed = [...a.removed];
  for (const fp of b.removed) {
    const i = added.findIndex((x) => ledgerFp(x.row) === fp);
    if (i >= 0) added.splice(i, 1);
    else removed.push(fp);
  }
  return { added: [...added, ...b.added], removed };
}

/** Re-apply the delta onto the server's CURRENT array. Removals take out one
 * matching row per fingerprint; adds skip rows already present (idempotent),
 * and land at the index the tab chose, clamped to the array. */
export function applyIntent(server: unknown[], intent: LedgerIntent): unknown[] {
  const out = [...server];
  for (const fp of intent.removed) {
    const i = out.findIndex((r) => ledgerFp(r) === fp);
    if (i >= 0) out.splice(i, 1);
  }
  const have = new Set(out.map(ledgerFp));
  // lowest index first so several front-inserts keep their relative order
  const adds = [...intent.added].sort((x, y) => x.at - y.at);
  for (const { row, at } of adds) {
    const fp = ledgerFp(row);
    if (have.has(fp)) continue;
    have.add(fp);
    out.splice(Math.max(0, Math.min(at, out.length)), 0, row);
  }
  return out;
}

export const isEmptyIntent = (i: LedgerIntent | undefined): boolean =>
  !i || (i.added.length === 0 && i.removed.length === 0);
